<template>
  <div class="ledger-page pb-5 pb-md-2">
    <b-card class="rounded-lg">
      <div class="d-flex justify-content-between align-items-center flex-wrap mb-1">
        <h4 class="mb-0">
          {{ $t('ledger_sub_title') }}
        </h4>
        <b-button
          v-if="canEdit"
          variant="primary"
          class="rounded-pill"
          @click="openCreate"
        >
          {{ $t('ledger_sub_new') }}
        </b-button>
      </div>
      <b-row>
        <b-col
          cols="12"
          md="4"
        >
          <b-form-input
            v-model="keyword"
            :placeholder="$t('ledger_search')"
            @keyup.enter="reload"
          />
        </b-col>
        <b-col
          cols="6"
          md="3"
        >
          <b-form-select
            v-model="isActive"
            :options="activeOptions"
            @change="reload"
          />
        </b-col>
      </b-row>
      <div
        v-if="loading"
        class="text-center py-3"
      >
        <b-spinner />
      </div>
      <div
        v-else-if="!rows.length"
        class="text-muted mt-1"
      >
        {{ $t('ledger_sub_empty') }}
      </div>
      <div v-else>
        <div class="d-none d-md-block mt-1">
          <b-table
            :items="rows"
            :fields="fields"
            small
            hover
            responsive
            class="ledger-dense-table"
          >
            <template #cell(name)="data">
              <b-link :to="{ name: 'ledger-subcontractors-view', params: { id: data.item.id } }">
                {{ data.item.name }}
              </b-link>
            </template>
            <template #cell(gl_account)="data">
              {{ glAccountText(data.item) || '—' }}
            </template>
            <template #cell(payable_balance)="data">
              <span class="ledger-num">{{ money(data.item.payable_balance) }}</span>
            </template>
            <template #cell(retention_balance)="data">
              <span class="ledger-num">{{ money(data.item.retention_balance) }}</span>
            </template>
          </b-table>
        </div>
        <div class="d-md-none mt-1">
          <article
            v-for="item in rows"
            :key="item.id"
            class="ledger-sub-card"
            @click="$router.push({ name: 'ledger-subcontractors-view', params: { id: item.id } })"
          >
            <strong>{{ item.name }}</strong>
            <div class="small text-muted">
              {{ glAccountText(item) || '—' }}
            </div>
            <div class="ledger-num">
              2115 {{ money(item.payable_balance) }}
            </div>
            <div class="ledger-num">
              2120 {{ money(item.retention_balance) }}
            </div>
          </article>
        </div>
        <b-pagination
          v-if="meta.total"
          v-model="page"
          class="mt-1"
          align="center"
          :total-rows="meta.total"
          :per-page="meta.per_page || 15"
          @change="onPage"
        />
      </div>
    </b-card>
    <b-modal
      v-model="formOpen"
      :title="$t('ledger_sub_new')"
      hide-footer
    >
      <subcontractor-form
        @saved="onSaved"
        @cancel="formOpen = false"
      />
    </b-modal>
    <div
      v-if="rows.length"
      class="ledger-sub-sticky d-md-none"
    >
      <span>2115</span>
      <strong class="ledger-num">{{ money(payableSum) }}</strong>
    </div>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BFormInput, BFormSelect, BButton, BTable, BLink, BSpinner, BPagination, BModal,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { getSubcontractors } from '@/api/accounting/subcontractors'
import {
  listFrom, formatLedgerMoney, roundMoney, apiErrorMessage, glAccountText,
} from '@/utils/ledger'
import SubcontractorForm from './SubcontractorForm.vue'

export default {
  name: 'SubcontractorList',
  components: {
    BCard, BRow, BCol, BFormInput, BFormSelect, BButton, BTable, BLink, BSpinner, BPagination, BModal, SubcontractorForm,
  },
  data() {
    return {
      loading: false,
      rows: [],
      meta: {},
      keyword: '',
      isActive: '',
      page: 1,
      formOpen: false,
    }
  },
  computed: {
    canEdit() {
      return this.hasPermission('edit subcontractors')
    },
    activeOptions() {
      return [
        { value: '', text: this.$t('ledger_assets_all_statuses') },
        { value: '1', text: this.$t('ledger_sub_active') },
        { value: '0', text: this.$t('ledger_sub_inactive') },
      ]
    },
    fields() {
      return [
        { key: 'name', label: this.$t('ledger_sub_name') },
        { key: 'gl_account', label: this.$t('ledger_gl_account') },
        { key: 'specialty', label: this.$t('ledger_sub_specialty') },
        { key: 'payable_balance', label: this.$t('ledger_sub_payable') },
        { key: 'retention_balance', label: this.$t('ledger_sub_retention') },
      ]
    },
    payableSum() {
      return roundMoney(this.rows.reduce((sum, row) => sum + (Number(row.payable_balance) || 0), 0))
    },
  },
  mounted() {
    this.load()
  },
  methods: {
    glAccountText,
    money(value) {
      return formatLedgerMoney(value || 0, this.$i18n.locale)
    },
    openCreate() {
      this.formOpen = true
    },
    onSaved(row) {
      this.formOpen = false
      if (row && row.id) this.$router.push({ name: 'ledger-subcontractors-view', params: { id: row.id } })
      else this.reload()
    },
    reload() {
      this.page = 1
      this.load()
    },
    onPage(page) {
      this.page = page
      this.load()
    },
    async load() {
      this.loading = true
      try {
        const params = { page: this.page, per_page: 15 }
        if (this.keyword) params.keyword = this.keyword.trim()
        if (this.isActive !== '') params.is_active = this.isActive
        const res = await getSubcontractors(params)
        this.rows = listFrom(res)
        this.meta = res.meta || {}
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: apiErrorMessage(error, this.$t('ledger_error')),
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.ledger-num { font-variant-numeric: tabular-nums; }
.ledger-sub-card { border-bottom: 1px solid #eee; padding: 0.85rem 0.15rem; cursor: pointer; }
.ledger-sub-sticky {
  position: fixed; bottom: 0; inset-inline: 0; z-index: 20; display: flex;
  justify-content: space-between; background: #fff; border-top: 1px solid #e6e6e6;
  padding: 0.75rem 1rem calc(0.75rem + env(safe-area-inset-bottom));
}
</style>
